import { FC, useMemo, useCallback } from 'react'
import { Card, Select, InputNumber, Input, Button, Badge, Popconfirm } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import {
  ICustomerSalesData,
  ILocalPaymentEntry,
  PaymentMethod,
} from '@/supabase/DailySalesSummary/dailySalesSummary.interface'

const tableStyles = `
  .payment-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }
  .payment-table th {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    padding: 10px 12px;
    text-align: center;
    font-weight: 600;
    color: #475569;
    white-space: nowrap;
  }
  .payment-table th.text-right { text-align: right; }
  .payment-table th.text-left { text-align: left; }

  /* Customer data row */
  .payment-table .customer-row td {
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
  .payment-table .customer-row:hover td {
    background: #f8fafc;
  }

  /* Payment entry sub-row — no border, tighter padding */
  .payment-table .payment-entry-row td {
    padding: 4px 12px;
    border: none;
    background: #fafbfc;
  }
  .payment-table .payment-entry-row:hover td {
    background: #f1f5f9;
  }

  /* Add-payment button row */
  .payment-table .add-payment-row td {
    padding: 4px 12px 10px;
    border-bottom: 1px solid #e2e8f0;
    background: #fafbfc;
  }

  /* Group header row */
  .payment-table .group-header-row td {
    padding: 8px 12px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 700;
    font-size: 13px;
  }

  /* Summary rows */
  .payment-table .summary-row td {
    padding: 10px 12px;
    background: #f8fafc;
    border-top: 2px solid #e2e8f0;
    font-weight: 700;
  }
  .payment-table .summary-row-total td {
    padding: 10px 12px;
    background: #eff6ff;
    border-top: 1px solid #e2e8f0;
    font-weight: 600;
  }

  /* Voided row indicator */
  .payment-table .voided-row td {
    opacity: 0.5;
    text-decoration: line-through;
  }

  /* Add-payment row in voided state: no line-through, no opacity reduction (buttons must be readable) */
  .payment-table .voided-row.add-payment-row td {
    opacity: 1;
    text-decoration: none;
  }

  /* Unsaved indicator */
  .unsaved-indicator {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #f59e0b;
    border-radius: 2px;
  }

  /* Financial input alignment */
  .financial-input .ant-input-number-input {
    text-align: right !important;
    padding-right: 10px !important;
  }

  /* Larger font size inside all payment input boxes */
  .payment-entry-inputs .ant-input-number-input {
    font-size: 16px !important;
    font-weight: 500;
  }
  .payment-entry-inputs .ant-input-number {
    font-size: 16px !important;
  }
  .payment-entry-inputs .ant-input-number-prefix {
    font-size: 16px !important;
  }
  .payment-entry-inputs .ant-select-selection-item {
    font-size: 16px !important;
    font-weight: 500;
  }
  .payment-entry-inputs .ant-select-selector {
    font-size: 16px !important;
  }
  .payment-entry-inputs .ant-input {
    font-size: 16px !important;
  }

  /* Table container */
  .table-container {
    width: 100%;
    overflow-x: auto;
  }
`

interface CustomerSalesTableProps {
  customers: ICustomerSalesData[]
  loading?: boolean
  addPaymentEntry: (rowKey: string) => string
  updatePaymentEntry: (rowKey: string, tempId: string, updates: Partial<ILocalPaymentEntry>) => void
  deletePaymentEntry: (rowKey: string, tempId: string) => void
  localPaymentEntries: Map<string, ILocalPaymentEntry[]>
  hasPaymentChanges: boolean
  onToggleVoid?: (orderId: number, isVoided: boolean) => void
}

// Generate a unique rowKey for each customer row
const getRowKey = (c: ICustomerSalesData): string =>
  c.rowType === 'ORDER' && c.orderId != null
    ? `${c.customerId}-${c.orderId}`
    : `${c.customerId}-payment`

// Total columns count for colSpan
const TOTAL_COLS = 10

export const CustomerSalesTable: FC<CustomerSalesTableProps> = ({
  customers,
  loading = false,
  addPaymentEntry,
  updatePaymentEntry,
  deletePaymentEntry,
  localPaymentEntries,
  hasPaymentChanges: _hasPaymentChanges,
  onToggleVoid,
}) => {
  // Separate customers by type
  const { regularCustomers, generalCustomers } = useMemo(() => {
    const regular = customers.filter(c => c.customerType === 'REGULAR')
    const general = customers.filter(c => c.customerType === 'GENERAL')
    return { regularCustomers: regular, generalCustomers: general }
  }, [customers])

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount)

  const formatQuantity = (quantity: number) =>
    new Intl.NumberFormat('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(quantity)

  // Get effective payment entries (local state or server data)
  const getEffectivePaymentEntries = useCallback((rowKey: string, serverEntries: ICustomerSalesData['paymentEntries']) => {
    const localEntries = localPaymentEntries.get(rowKey)
    if (localEntries && localEntries.length > 0) {
      return localEntries.filter(e => e.status !== 'deleted')
    }
    return serverEntries.map(e => ({
      tempId: e.id,
      id: e.id,
      dailyCustomerSummaryId: e.dailyCustomerSummaryId,
      amountTHB: e.amountTHB,
      paymentMethod: e.paymentMethod,
      note: e.note,
      status: 'unchanged' as const,
    }))
  }, [localPaymentEntries])

  const getEffectivePaidAmount = useCallback((rowKey: string, serverEntries: ICustomerSalesData['paymentEntries']) => {
    const entries = getEffectivePaymentEntries(rowKey, serverEntries)
    return entries.reduce((sum, e) => sum + e.amountTHB, 0)
  }, [getEffectivePaymentEntries])

  const hasUnsavedChanges = useCallback((rowKey: string) => {
    const localEntries = localPaymentEntries.get(rowKey)
    if (!localEntries) return false
    return localEntries.some(e => e.status !== 'unchanged')
  }, [localPaymentEntries])

  // Summary totals — filter out voided orders, then aggregate per customer to avoid double-counting
  const summaryTotals = useMemo(() => {
    const activeCustomers = customers.filter(c => !c.isVoidedFromSales)

    // Calculate outstanding per-row (same logic as Excel and calculatePaymentSummary):
    // outstanding = max(0, row.totalAmountTHB - row.paidFromEntries)
    // This prevents payment on a zero-amount row from reducing outstanding of other rows.
    let totalPaid = 0
    let totalOutstanding = 0

    for (const c of activeCustomers) {
      const rowKey = getRowKey(c)
      const paidAmount = getEffectivePaidAmount(rowKey, c.paymentEntries)
      totalPaid += paidAmount
      totalOutstanding += Math.max(0, c.totalAmountTHB - paidAmount)
    }

    return { totalPaid, totalOutstanding }
  }, [customers, getEffectivePaidAmount])

  // Render rows for a list of customers (each row = 1 order)
  const renderCustomerRows = useCallback((orderRows: ICustomerSalesData[], startIndex: number) => {
    const rows: JSX.Element[] = []
    let idx = startIndex

    for (const customer of orderRows) {
      const rowKey = getRowKey(customer)
      const entries = getEffectivePaymentEntries(rowKey, customer.paymentEntries)
      const paidAmount = getEffectivePaidAmount(rowKey, customer.paymentEntries)
      const outstanding = Math.max(0, customer.totalAmountTHB - paidAmount)
      const changed = hasUnsavedChanges(rowKey)
      const totalCrushed = customer.crushedIceAmount + customer.coarselyCrushedIceAmount
      const hasOrders = customer.totalAmountTHB > 0
      idx++

      // Use real entries if they exist, otherwise show a UI-only placeholder (no state mutation)
      // This avoids triggering unsaved-changes indicator on untouched rows
      const displayEntries = entries.length > 0 ? entries : [{
        tempId: '__placeholder__',
        amountTHB: 0,
        paymentMethod: PaymentMethod.CASH,
        note: undefined as string | undefined,
        status: 'new' as const,
      }]

      // Helper to render a single payment entry input row
      const renderEntryInputs = (entry: typeof displayEntries[0], entryIndex: number) => {
        const isPlaceholder = entry.tempId === '__placeholder__'
        // First entry (index 0): hide delete if customer has orders
        const showDelete = entryIndex > 0 || !hasOrders

        const handleAmountChange = (v: number | null) => {
          if (isPlaceholder) {
            const newTempId = addPaymentEntry(rowKey)
            if (v !== null && v !== undefined) {
              updatePaymentEntry(rowKey, newTempId, { amountTHB: v })
            }
          } else {
            updatePaymentEntry(rowKey, entry.tempId, { amountTHB: v || 0 })
          }
        }

        const handleMethodChange = (v: string) => {
          if (isPlaceholder) {
            const newTempId = addPaymentEntry(rowKey)
            updatePaymentEntry(rowKey, newTempId, { paymentMethod: v as PaymentMethod })
          } else {
            updatePaymentEntry(rowKey, entry.tempId, { paymentMethod: v as PaymentMethod })
          }
        }

        const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (isPlaceholder) {
            const newTempId = addPaymentEntry(rowKey)
            updatePaymentEntry(rowKey, newTempId, { note: e.target.value })
          } else {
            updatePaymentEntry(rowKey, entry.tempId, { note: e.target.value })
          }
        }

        // Use a stable key: for the first entry slot, use rowKey so the key never changes
        // when transitioning from placeholder → real entry. For additional entries, use tempId.
        const stableKey = entryIndex === 0 ? `${rowKey}-0` : entry.tempId

        return (
          <div className="flex items-center gap-1 payment-entry-inputs" key={stableKey}>
            <InputNumber
              value={entry.amountTHB === 0 ? null : entry.amountTHB}
              onChange={handleAmountChange}
              placeholder="0"
              min={0}
              size="middle"
              className="financial-input"
              style={{ width: 110 }}
              prefix="฿"
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={v => parseFloat(v!.replace(/\$\s?|(,*)/g, '')) || 0}
            />
            <Select
              value={entry.paymentMethod}
              onChange={handleMethodChange}
              size="middle"
              style={{ width: 80 }}
              options={[
                { value: PaymentMethod.CASH, label: 'สด' },
                { value: PaymentMethod.TRANSFER, label: 'โอน' },
              ]}
            />
            <Input
              value={entry.note || ''}
              onChange={handleNoteChange}
              placeholder="หมายเหตุ"
              size="middle"
              style={{ width: 120 }}
            />
            {showDelete && !isPlaceholder && (
              <Button
                type="text"
                danger
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => deletePaymentEntry(rowKey, entry.tempId)}
                className="cursor-pointer"
              />
            )}
            {(!showDelete || isPlaceholder) && <div style={{ width: 32 }} />}
          </div>
        )
      }

      const isVoided = customer.isVoidedFromSales
      const voidedClass = isVoided ? ' voided-row' : ''

      // Main customer row — first payment entry inline on same row
      rows.push(
        <tr key={`customer-${rowKey}`} className={`customer-row${voidedClass}`}>
          <td style={{ position: 'relative', textAlign: 'center' }}>
            {changed && <div className="unsaved-indicator" />}
            {idx}
          </td>
          <td>
            <div className="flex items-center gap-1">
              {changed && <Badge status="warning" />}
              <span className="font-medium">{customer.customerName}</span>
            </div>
          </td>
          <td style={{ textAlign: 'right' }}>{formatQuantity(customer.largeIceCubeAmount)}</td>
          <td style={{ textAlign: 'right' }}>{formatQuantity(customer.smallIceCubeAmount)}</td>
          <td style={{ textAlign: 'right' }}>{formatQuantity(totalCrushed)}</td>
          <td style={{ textAlign: 'right' }}>
            <span className="font-semibold text-blue-600">{formatQuantity(customer.totalQuantity)}</span>
          </td>
          <td style={{ textAlign: 'right' }}>
            <span className="font-semibold text-green-600">{formatCurrency(customer.totalAmountTHB)}</span>
          </td>
          <td style={{ padding: '6px 8px' }}>
            {renderEntryInputs(displayEntries[0], 0)}
          </td>
          <td style={{ textAlign: 'right' }}>
            {paidAmount === 0
              ? <span className="text-gray-400">฿0</span>
              : <span className="font-semibold text-green-600">{formatCurrency(paidAmount)}</span>
            }
          </td>
          <td style={{ textAlign: 'right' }}>
            <span className={`font-semibold ${outstanding > 0 ? 'text-red-500' : 'text-green-600'}`}>
              {formatCurrency(outstanding)}
            </span>
          </td>
        </tr>
      )

      // Additional payment entry rows (2nd, 3rd, etc.)
      for (let i = 1; i < displayEntries.length; i++) {
        rows.push(
          <tr key={`payment-${rowKey}-${displayEntries[i].tempId}`} className={`payment-entry-row${voidedClass}`}>
            <td colSpan={7} />
            <td style={{ padding: '4px 8px' }}>
              {renderEntryInputs(displayEntries[i], i)}
            </td>
            <td colSpan={2} />
          </tr>
        )
      }

      // Add payment button row — aligned to the right of payment column
      const isOrderRow = customer.rowType === 'ORDER' && customer.orderId != null
      rows.push(
        <tr key={`add-${rowKey}`} className={`add-payment-row${voidedClass}`}>
          <td colSpan={7} />
          <td style={{ padding: '4px 8px', textAlign: 'right' }}>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="link"
                size="middle"
                icon={<PlusOutlined />}
                onClick={() => {
                  addPaymentEntry(rowKey)
                }}
                className="cursor-pointer"
                style={{ fontSize: 12, color: '#3b82f6' }}
              >
                เพิ่มรายการ
              </Button>
              {isOrderRow && onToggleVoid && (
                customer.isVoidedFromSales ? (
                  <Popconfirm
                    title="คืนรายการ"
                    description="ออเดอร์จะถูกนำกลับมาคำนวณยอดขาย"
                    onConfirm={() => onToggleVoid(customer.orderId!, !customer.isVoidedFromSales)}
                    okText="ยืนยัน"
                    cancelText="ยกเลิก"
                  >
                    <Button type="link" size="small" className="cursor-pointer" style={{ fontSize: 12, color: '#3b82f6' }}>
                      คืนรายการ
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="ยกเลิกรายการ"
                    description="ออเดอร์จะไม่ถูกนำมาคำนวณยอดขาย"
                    onConfirm={() => onToggleVoid(customer.orderId!, !customer.isVoidedFromSales)}
                    okText="ยืนยัน"
                    cancelText="ยกเลิก"
                  >
                    <Button type="link" size="small" className="cursor-pointer" style={{ fontSize: 12, color: '#f87171' }}>
                      ยกเลิกรายการ
                    </Button>
                  </Popconfirm>
                )
              )}
            </div>
          </td>
          <td colSpan={2} />
        </tr>
      )
    }

    return { rows, nextIndex: idx }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEffectivePaymentEntries, getEffectivePaidAmount, hasUnsavedChanges, updatePaymentEntry, deletePaymentEntry, addPaymentEntry, onToggleVoid])

  if (customers.length === 0 && !loading) {
    return null
  }

  // Build all rows
  const allRows: JSX.Element[] = []
  let currentIndex = 0

  if (regularCustomers.length > 0) {
    allRows.push(
      <tr key="group-regular" className="group-header-row">
        <td colSpan={TOTAL_COLS}>
          <span className="text-blue-600">ลูกค้าประจำ</span>
        </td>
      </tr>
    )
    const result = renderCustomerRows(regularCustomers, currentIndex)
    allRows.push(...result.rows)
    currentIndex = result.nextIndex
  }

  if (generalCustomers.length > 0) {
    allRows.push(
      <tr key="group-general" className="group-header-row">
        <td colSpan={TOTAL_COLS}>
          <span className="text-green-600">ลูกค้าทั่วไป</span>
        </td>
      </tr>
    )
    const result = renderCustomerRows(generalCustomers, currentIndex)
    allRows.push(...result.rows)
  }

  // Totals — filter out voided orders
  const activeCustomers = customers.filter(c => !c.isVoidedFromSales)
  const totalLarge = activeCustomers.reduce((s, c) => s + c.largeIceCubeAmount, 0)
  const totalSmall = activeCustomers.reduce((s, c) => s + c.smallIceCubeAmount, 0)
  const totalCrushed = activeCustomers.reduce((s, c) => s + c.crushedIceAmount + c.coarselyCrushedIceAmount, 0)
  const totalQty = activeCustomers.reduce((s, c) => s + c.totalQuantity, 0)
  const totalAmount = activeCustomers.reduce((s, c) => s + c.totalAmountTHB, 0)
  const totalAllQty = activeCustomers.reduce((s, c) => s + c.largeIceCubeAmount + c.smallIceCubeAmount + c.crushedIceAmount + c.coarselyCrushedIceAmount, 0)

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: tableStyles }} />
      <Card className="overflow-hidden" styles={{ body: { padding: 0 } }}>
        <div className="table-container">
          <table className="payment-table" style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 50 }}>ลำดับ</th>
                <th className="text-left" style={{ width: 140 }}>ชื่อลูกค้า</th>
                <th className="text-right" style={{ width: 70 }}>ใหญ่</th>
                <th className="text-right" style={{ width: 70 }}>เล็ก</th>
                <th className="text-right" style={{ width: 70 }}>โม่</th>
                <th className="text-right" style={{ width: 80 }}>รวม</th>
                <th className="text-right" style={{ width: 110 }}>จำนวนเงินรวม</th>
                <th style={{ minWidth: 380 }}>ชำระเงิน</th>
                <th className="text-right" style={{ width: 100 }}>จ่ายแล้ว</th>
                <th className="text-right" style={{ width: 100 }}>ค้างจ่าย</th>
              </tr>
            </thead>
            <tbody>
              {allRows}
            </tbody>
            <tfoot>
              <tr className="summary-row">
                <td colSpan={2} style={{ textAlign: 'left' }}>
                  <span className="text-gray-800">รวมทั้งหมด</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-blue-600">{formatQuantity(totalLarge)}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-blue-600">{formatQuantity(totalSmall)}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-blue-600">{formatQuantity(totalCrushed)}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-blue-600">{formatQuantity(totalQty)}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-green-600">{formatCurrency(totalAmount)}</span>
                </td>
                <td />
                <td style={{ textAlign: 'right' }}>
                  <span className="text-green-600">{formatCurrency(summaryTotals.totalPaid)}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className="text-red-500">{formatCurrency(summaryTotals.totalOutstanding)}</span>
                </td>
              </tr>
              <tr className="summary-row-total">
                <td colSpan={TOTAL_COLS} style={{ textAlign: 'left' }}>
                  <span className="text-blue-700 text-base">
                    รวมขาย {formatQuantity(totalAllQty)} กระสอบ
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  )
}
